import React from 'react';
import {
  BarChart3,
  ChevronRight,
  Database,
  MapPin,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { REPORT_TYPE_REGISTRY } from '../../../config/reportTypes';
import { DECISION_ANALYSIS_BAND_LABEL } from '../../../lib/portfolio/decisionBands';
import {
  validateGenerationLayoutExhibitSnapshot,
} from '../../../lib/generationImpact/generationLayoutExhibit';
import {
  renderGenerationLayoutExhibitSvg,
} from '../../../lib/generationImpact/generationLayoutExhibitSvg';
import { cn } from '../../../lib/utils';
import {
  isWithheldNarrativeNotice,
  scrubInternalSentinelsDeep,
} from '../../../lib/reportRendering/scrubInternalSentinels';
import { stripConfidenceCommentsDeep } from '../../../lib/reportRendering/narrativeArtifacts';
import { WithheldNarrativeNotice } from '../report/WithheldNarrativeNotice';
import { isWithheldSectionOmissionDetail } from '../../../lib/report/withheldNarrative';
import type {
  DecisionAnalysisSectionV1,
  DecisionAnalysisSnapshotV1,
} from '../../../types/decisionAnalysis';
import type {
  EvidenceValue,
  JsonValue,
  ReportDocumentBlockV1,
} from '../../../types/reportContractsV2';
import type { SiteRagReport } from '../../../types/siteRag';
import { StatusPill } from '../../ui/status-pill';
import { analysisCardSummary } from './analysisCardSummary';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';

const presentationAnalysisCache = new WeakMap<DecisionAnalysisSnapshotV1, DecisionAnalysisSnapshotV1>();

/**
 * The analysis snapshot as the dossier PRESENTS it. Snapshots frozen before
 * 2026-09-18 can carry the reviewer-voice gate's internal ops sentinel
 * ("… unavailable — content failed quality gate. Regenerate report.") in
 * summary, blocks or claims; every string leaf is deep-scrubbed to the honest
 * "not assessed" notice. Presentation copy only — never verify its identity
 * or persist it (the immutable snapshot is untouched). Memoised per snapshot
 * object, so repeated calls in one render cost one walk.
 */
export function analysisForReport(report: SiteRagReport): DecisionAnalysisSnapshotV1 | null {
  const snapshot = report.decisionAnalysis ?? null;
  if (snapshot === null) return null;
  const cached = presentationAnalysisCache.get(snapshot);
  if (cached) return cached;
  const presented = stripConfidenceCommentsDeep(scrubInternalSentinelsDeep(snapshot));
  presentationAnalysisCache.set(snapshot, presented);
  return presented;
}

function scalarText(value: JsonValue): string {
  if (value === null) return 'Not specified';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return JSON.stringify(value);
}

function evidenceText(value: EvidenceValue): string {
  const unit = value.unit ? ` ${value.unit}` : '';
  if (value.state === 'known') return `${scalarText(value.value)}${unit}`;
  if (value.state === 'conflict') {
    return `Conflict — ${value.reason}: ${value.candidates.map(scalarText).join(' / ')}`;
  }
  return `${value.state === 'unknown' ? 'Unknown' : 'Not applicable'} — ${value.reason}`;
}

function Provenance({
  block,
}: {
  block: ReportDocumentBlockV1;
}): React.ReactElement | null {
  if (block.sourceIds.length === 0 && block.citationIds.length === 0) return null;
  return (
    <p className="mt-2 flex flex-wrap gap-1.5 type-meta text-text-muted">
      {block.sourceIds.map(sourceId => (
        <span
          key={`source:${sourceId}`}
          className="rounded border border-border-subtle bg-background px-1.5 py-0.5"
        >
          Source · {sourceId}
        </span>
      ))}
      {block.citationIds.map(citationId => (
        <span
          key={`citation:${citationId}`}
          className="rounded border border-border-subtle bg-background px-1.5 py-0.5"
        >
          Citation · {citationId}
        </span>
      ))}
    </p>
  );
}

function recordValue(value: JsonValue): value is Record<string, JsonValue> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function tableRows(value: JsonValue): Array<Record<string, JsonValue>> | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  return value.every(recordValue) ? value : null;
}

interface NumericDatum {
  label: string;
  value: number;
}

function numericData(
  value: JsonValue,
  prefix = '',
  depth = 0,
): NumericDatum[] {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return [{ label: prefix || 'Value', value }];
  }
  if (Array.isArray(value) && depth < 2) {
    return value.flatMap((entry, index) => {
      if (typeof entry === 'number' && Number.isFinite(entry)) {
        return [{ label: prefix ? `${prefix} · ${index + 1}` : `${index + 1}`, value: entry }];
      }
      if (!recordValue(entry)) return [];
      const rowLabel = [
        entry.label,
        entry.name,
        entry.category,
        entry.id,
        entry.year,
        entry.date,
      ].find(candidate => typeof candidate === 'string' || typeof candidate === 'number');
      const label = rowLabel == null ? `${index + 1}` : scalarText(rowLabel);
      return Object.entries(entry).flatMap(([key, nested]) => (
        typeof nested === 'number' && Number.isFinite(nested)
          ? [{
              label: prefix
                ? `${prefix} · ${label} · ${key}`
                : `${label} · ${key}`,
              value: nested,
            }]
          : []
      ));
    });
  }
  if (!recordValue(value) || depth >= 2) return [];
  return Object.entries(value).flatMap(([key, nested]) =>
    numericData(nested, prefix ? `${prefix} · ${key}` : key, depth + 1));
}

function coordinateFromValue(
  value: JsonValue,
  depth = 0,
): { lat: number; lng: number } | null {
  if (!recordValue(value) || depth > 3) return null;
  const lat = value.lat ?? value.latitude;
  const lng = value.lng ?? value.lon ?? value.longitude;
  if (
    typeof lat === 'number'
    && Number.isFinite(lat)
    && lat >= -90
    && lat <= 90
    && typeof lng === 'number'
    && Number.isFinite(lng)
    && lng >= -180
    && lng <= 180
  ) return { lat, lng };
  for (const nested of Object.values(value)) {
    const coordinate = coordinateFromValue(nested, depth + 1);
    if (coordinate) return coordinate;
  }
  return null;
}

function StructuredValue({
  value,
  role,
  label,
}: {
  value: JsonValue;
  role: DecisionAnalysisSectionV1['role'];
  label: string;
}): React.ReactElement {
  const coordinate = role === 'map' ? coordinateFromValue(value) : null;
  if (coordinate) {
    return (
      <figure
        className="overflow-hidden rounded-lg border border-border-subtle"
        data-testid="findings-html-map"
      >
        <div
          className="relative flex min-h-44 items-center justify-center bg-[radial-gradient(circle_at_center,rgba(25,118,210,0.12),transparent_45%),linear-gradient(to_right,rgba(148,163,184,0.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.10)_1px,transparent_1px)] bg-[size:auto,24px_24px,24px_24px]"
          role="img"
          aria-label={`${label} location at latitude ${coordinate.lat}, longitude ${coordinate.lng}`}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary shadow-card">
            <MapPin className="h-5 w-5" aria-hidden />
          </span>
        </div>
        <figcaption className="flex flex-wrap items-center justify-between gap-2 border-t border-border-subtle px-3 py-2 text-xs text-text-secondary">
          <span>{label}</span>
          <span className="font-mono tabular-nums text-text-muted">
            {coordinate.lat.toFixed(5)}, {coordinate.lng.toFixed(5)}
          </span>
        </figcaption>
      </figure>
    );
  }

  const numeric = role === 'chart' ? numericData(value).slice(0, 24) : [];
  if (numeric.length > 0) {
    const maximum = Math.max(...numeric.map(item => Math.abs(item.value)), 1);
    return (
      <figure
        className="rounded-lg border border-border-subtle bg-background/45 p-3"
        data-testid="findings-html-chart"
        aria-label={`${label} chart`}
      >
        <div className="mb-3 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" aria-hidden />
          <figcaption className="text-xs font-semibold text-text-primary">{label}</figcaption>
        </div>
        <div className="space-y-2.5">
          {numeric.map(item => (
            <div key={item.label}>
              <div className="mb-1 flex items-baseline justify-between gap-3 text-xs">
                <span className="truncate text-text-secondary">{item.label}</span>
                <span className="font-mono tabular-nums text-text-primary">
                  {scalarText(item.value)}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-state-hover">
                <div
                  className={cn(
                    'h-full rounded-full',
                    item.value < 0 ? 'bg-danger' : 'bg-primary',
                  )}
                  style={{ width: `${Math.max(1, Math.abs(item.value) / maximum * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </figure>
    );
  }

  const rows = tableRows(value);
  if (rows) {
    const columns = [...new Set(rows.flatMap(row => Object.keys(row)))].slice(0, 12);
    return (
      <div className="overflow-x-auto rounded-lg border border-border-subtle">
        <Table data-testid="findings-html-structured-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {columns.map(column => (
                <TableHead key={column} className="text-xs text-text-muted">
                  {column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice(0, 100).map((row, index) => (
              <TableRow key={`${label}:${index}`}>
                {columns.map(column => (
                  <TableCell key={column} className="whitespace-normal text-xs">
                    {scalarText(row[column] ?? null)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (recordValue(value)) {
    return (
      <dl
        className="grid gap-px overflow-hidden rounded-lg border border-border-subtle bg-border-subtle sm:grid-cols-2"
        data-testid="findings-html-structured-data"
      >
        {Object.entries(value).slice(0, 100).map(([key, nested]) => (
          <div key={key} className="bg-background px-3 py-2.5">
            <dt className="type-meta text-text-muted">{key}</dt>
            <dd className="mt-1 break-words text-xs text-text-primary">
              {scalarText(nested)}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return <p className="text-sm text-text-secondary">{scalarText(value)}</p>;
}

function AnalysisBlock({
  block,
  section,
}: {
  block: ReportDocumentBlockV1;
  section: DecisionAnalysisSectionV1;
}): React.ReactElement {
  let body: React.ReactElement;
  switch (block.kind) {
    case 'narrative':
      body = isWithheldNarrativeNotice(block.markdown)
        ? <WithheldNarrativeNotice variant="dossier" />
        : (
          <div className="prose prose-sm max-w-none text-text-secondary prose-headings:text-text-primary prose-a:text-primary">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{block.markdown}</ReactMarkdown>
          </div>
        );
      break;
    case 'kpi':
      body = (
        <dl
          className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3"
          data-testid="findings-html-kpis"
        >
          {block.items.map(item => (
            <div key={item.id} className="rounded-lg border border-border-subtle bg-background px-3 py-2.5">
              <dt className="type-meta text-text-muted">{item.label}</dt>
              <dd className="mt-1 text-sm font-semibold text-text-primary">
                {evidenceText(item.value)}
              </dd>
            </div>
          ))}
        </dl>
      );
      break;
    case 'table':
      body = (
        <div className="overflow-x-auto rounded-lg border border-border-subtle">
          <Table data-testid="findings-html-table">
            {block.caption ? <caption className="p-2 text-left text-xs text-text-muted">{block.caption}</caption> : null}
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {block.columns.map(column => (
                  <TableHead key={column.id} className="text-xs text-text-muted">
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {block.rows.map(row => (
                <TableRow key={row.id}>
                  {block.columns.map(column => (
                    <TableCell key={column.id} className="whitespace-normal text-xs">
                      {evidenceText(row.cells[column.id] ?? {
                        state: 'unknown',
                        reason: 'No value supplied',
                        sourceIds: [],
                      })}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
      break;
    case 'list': {
      const List = block.ordered ? 'ol' : 'ul';
      body = (
        <List className={cn(
          'space-y-1.5 pl-5 text-sm leading-relaxed text-text-secondary',
          block.ordered ? 'list-decimal' : 'list-disc',
        )}>
          {block.items.map(item => (
            <li key={item.id}>
              {isWithheldNarrativeNotice(item.text)
                ? <WithheldNarrativeNotice variant="inline" />
                : item.text}
            </li>
          ))}
        </List>
      );
      break;
    }
    case 'structured-data':
      body = <StructuredValue value={block.value} role={section.role} label={block.label} />;
      break;
    case 'legal':
      body = (
        <p className="rounded-lg border border-border-subtle bg-background px-3 py-2.5 text-xs leading-relaxed text-text-muted">
          {block.text}
        </p>
      );
      break;
  }

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      {body}
      <Provenance block={block} />
    </div>
  );
}

function revisionLabel(analysis: DecisionAnalysisSnapshotV1): string {
  return `${analysis.analysisId} · ${analysis.contentHash.slice(0, 10)}`;
}

function FrozenGenerationLayoutExhibit({
  report,
  label,
}: {
  report: SiteRagReport;
  label: string;
}): React.ReactElement | null {
  const exhibit = report.generationLayoutExhibitAtGen;
  if (!exhibit) return null;
  const validation = validateGenerationLayoutExhibitSnapshot(exhibit);
  if (!validation.valid) {
    return (
      <p
        className="mb-3 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2.5 text-xs leading-relaxed text-warning"
        role="status"
      >
        The frozen layout graphic is withheld because its immutable identity
        could not be verified. The semantic findings below remain visible.
      </p>
    );
  }

  let svgMarkup: string;
  try {
    svgMarkup = renderGenerationLayoutExhibitSvg(exhibit);
  } catch {
    return (
      <p
        className="mb-3 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2.5 text-xs leading-relaxed text-warning"
        role="status"
      >
        The verified layout data is available, but its web graphic could not be
        rendered. No live geometry was substituted.
      </p>
    );
  }

  return (
    <figure
      className="mb-3 overflow-hidden rounded-lg border border-border-subtle bg-background"
      data-testid="findings-html-generation-layout"
    >
      <div
        className="[&>svg]:block [&>svg]:h-auto [&>svg]:w-full"
        role="img"
        aria-label={`${label} frozen generation layout`}
        // The renderer consumes a hash-validated, typed snapshot and emits the
        // same deterministic SVG used by the existing report projection.
        dangerouslySetInnerHTML={{ __html: svgMarkup }}
      />
      <figcaption className="border-t border-border-subtle px-3 py-2 text-xs leading-relaxed text-text-muted">
        Frozen parcel-fit geometry from this exact analysis revision. Opening
        Findings never re-runs the layout solver or substitutes live geometry.
      </figcaption>
    </figure>
  );
}

/**
 * Matrix-row expansion (2026-08-03): the standalone "Decision spine /
 * Analysis detail" section duplicated the Required Analysis Matrix — one
 * card per domain beside one row per domain, with a nav rail that was a
 * second copy of the matrix's row list. The per-analysis card now renders
 * INSIDE the expanded matrix row; the section and rail are gone.
 */
export function DecisionAnalysisExpansion({
  report,
}: {
  report: SiteRagReport;
}): React.ReactElement | null {
  const analysis = analysisForReport(report);
  if (analysis === null) return null;
  const analyses = [{ report, analysis }];
  return (
    <div className="min-w-0 space-y-4" data-testid="findings-domain-details">
      {analyses.map(({ report, analysis }) => (
          <details
            key={analysis.analysisId}
            id={`analysis-${analysis.analysisId}`}
            // Collapsed by default (2026-08-03): the expanded first body put a
            // full report inline on the cockpit page, duplicating the reader —
            // which now has its own Contents navigation. The summary row IS
            // the card: title, one-paragraph summary, QA + band pills.
            className="group scroll-mt-4 rounded-lg border border-border bg-card shadow-card"
          >
            <summary className="cursor-pointer list-none px-4 py-4 marker:hidden md:px-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="type-meta uppercase tracking-[0.12em] text-primary">
                    {REPORT_TYPE_REGISTRY[analysis.domain]?.shortLabel ?? analysis.domain}
                  </p>
                  <h3 className="mt-1 type-section text-text-primary">
                    {analysis.semantic.title}
                  </h3>
                  <p className="mt-1 max-w-3xl text-xs leading-relaxed text-text-muted">
                    {analysisCardSummary(analysis.semantic.summary)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <StatusPill variant={analysis.semantic.quality.state === 'pass' ? 'success' : 'warn'}>
                    QA · {analysis.semantic.quality.state}
                  </StatusPill>
                  {/* Canonical vocabulary, not the raw enum. This printed
                      `band.toUpperCase()` — "GO" / "CONDITIONAL" / "NO-GO" —
                      which is how the pre-Stage-4.1 words survived on a live
                      surface, and it rendered a WITHHELD verdict as the word
                      "WITHHELD" beside bands that read as decisions. */}
                  <StatusPill variant="neutral">
                    {DECISION_ANALYSIS_BAND_LABEL[analysis.semantic.systemDecision.band]}
                  </StatusPill>
                </div>
              </div>
              <p className="mt-2 truncate font-mono text-[10px] text-text-disabled">
                {revisionLabel(analysis)}
              </p>
            </summary>

            <div className="border-t border-border-subtle px-4 py-4 md:px-5">
              <div className="space-y-5">
                {analysis.semantic.sections.map(section => (
                  <section
                    key={section.id}
                    aria-labelledby={`${analysis.analysisId}-${section.id}-title`}
                    className="rounded-lg border border-border-subtle bg-background/30 p-3.5"
                  >
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <h4
                        id={`${analysis.analysisId}-${section.id}-title`}
                        className="text-sm font-semibold text-text-primary"
                      >
                        {section.title}
                      </h4>
                      <StatusPill variant="neutral">
                        {section.role}
                      </StatusPill>
                    </div>
                    {section.sectionId === 'generationLayoutExhibit' ? (
                      <FrozenGenerationLayoutExhibit
                        report={report}
                        label={section.title}
                      />
                    ) : null}
                    <div className="divide-y divide-border-subtle">
                      {section.blocks.map(block => (
                        <AnalysisBlock key={block.id} block={block} section={section} />
                      ))}
                    </div>
                  </section>
                ))}

                <section className="rounded-lg border border-border-subtle bg-background/30 p-3.5">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 text-primary" aria-hidden />
                    <h4 className="text-sm font-semibold text-text-primary">
                      Citations and provenance
                    </h4>
                  </div>
                  <p className="mt-1 text-xs text-text-muted">
                    {analysis.semantic.claims.length} claims · {analysis.semantic.citations.length} citations · {analysis.semantic.sources.length} source identities
                  </p>
                  {analysis.semantic.sources.length > 0 ? (
                    <ul className="mt-3 grid gap-2 md:grid-cols-2">
                      {analysis.semantic.sources.map(source => (
                        <li key={source.id} className="rounded-lg border border-border-subtle bg-background px-3 py-2 text-xs">
                          <p className="font-medium text-text-primary">
                            {source.uri ? (
                              <a
                                href={source.uri}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary underline-offset-2 hover:underline"
                              >
                                {source.label}
                              </a>
                            ) : source.label}
                          </p>
                          <p className="mt-0.5 text-text-muted">
                            {source.authority} · {source.kind}
                          </p>
                          {source.contentHash ? (
                            <p className="mt-1 truncate font-mono text-[10px] text-text-disabled">
                              SHA-256 {source.contentHash}
                            </p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {analysis.semantic.citations.length > 0 ? (
                    <div className="mt-4">
                      <h5 className="text-xs font-semibold text-text-primary">
                        Citations
                      </h5>
                      <ol className="mt-2 space-y-1.5">
                        {analysis.semantic.citations.map(citation => (
                          <li
                            key={citation.id}
                            id={`citation-${analysis.analysisId}-${citation.id}`}
                            className="rounded-lg border border-border-subtle bg-background px-3 py-2 text-xs text-text-secondary"
                          >
                            <span className="font-mono text-[10px] text-text-muted">
                              {citation.id}
                            </span>
                            <span className="mx-1.5 text-text-disabled">·</span>
                            {citation.url ? (
                              <a
                                href={citation.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary underline-offset-2 hover:underline"
                              >
                                {citation.label}
                              </a>
                            ) : citation.label}
                            {citation.retrievedAt ? (
                              <span className="ml-1.5 text-text-muted">
                                retrieved {citation.retrievedAt}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : null}
                  {analysis.semantic.claims.length > 0 ? (
                    <details className="mt-4 rounded-lg border border-border-subtle bg-background">
                      <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-text-primary">
                        Claim traceability · {analysis.semantic.claims.length}
                      </summary>
                      <ul className="divide-y divide-border-subtle border-t border-border-subtle">
                        {analysis.semantic.claims.map(claim => (
                          <li key={claim.id} className="px-3 py-2.5">
                            <p className="text-xs leading-relaxed text-text-primary">
                              {claim.text}
                            </p>
                            <p className="mt-1 font-mono text-[10px] text-text-muted">
                              Section {claim.sectionId}
                              {claim.sourceIds.length > 0
                                ? ` · Sources ${claim.sourceIds.join(', ')}`
                                : ''}
                              {claim.citationIds.length > 0
                                ? ` · Citations ${claim.citationIds.join(', ')}`
                                : ''}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                  {analysis.semantic.omissions.length > 0 ? (
                    <div className="mt-4">
                      <h5 className="text-xs font-semibold text-text-primary">
                        Explicit omissions
                      </h5>
                      <ul className="mt-2 space-y-1.5">
                        {analysis.semantic.omissions.map(omission => (
                          <li
                            key={omission.sectionId}
                            className="rounded-lg border border-warning/25 bg-warning/5 px-3 py-2 text-xs leading-relaxed text-text-secondary"
                          >
                            <span className="font-semibold text-text-primary">
                              {omission.sectionId}
                            </span>
                            {' — '}
                            {isWithheldSectionOmissionDetail(omission.detail) ? (
                              // A withheld narrative is not an evidence gap:
                              // no raw reason code, the designed notice.
                              <>
                                <WithheldNarrativeNotice variant="inline" />
                                {omission.required ? (
                                  <span className="ml-1.5 text-text-muted">(required)</span>
                                ) : null}
                              </>
                            ) : (
                              <>
                                {omission.detail}
                                <span className="ml-1.5 text-text-muted">
                                  ({omission.reason}{omission.required ? ', required' : ''})
                                </span>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </section>
              </div>
            </div>
          </details>
        ))}
    </div>
  );
}
